/* eslint-disable max-lines */
import { IconNames } from '@blueprintjs/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import { DateFormats, formatDate, sortAsc } from '@strata/helper-functions';
import {
    ChangeRequestSubType,
    MemberEscalationSubType,
    MemberInquiryEscalationOptionTree,
    RequestChatIcon,
    RequestType,
    RequestTypeDisplayTextMap,
    UserCell,
} from '@strata/react-chat';
import {
    type ColumnDef,
    type FilterCategory,
    GridWithFilters,
    PrimaryData,
    SecondaryData,
    TimeFrameOptions,
} from '@strata/react-grid';
import { Icon } from '@strata/react-status-icons';
import {
    type PayerRequestStatus,
    PayerRequestStatusIndicator,
} from '@strata/react-status-indicators';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';

import { PayerRequestNumber } from 'components/requests/PayerRequestNumber';
import { ViewsControls } from 'components/shared/ViewsControls';
import { ReadPermissionRequestTypeMap } from 'services/constants';
import { type TimeFrameOption } from 'services/interfaces';
import { AppRoutes } from 'services/routes';
import { useAppSelector } from 'store';
import { useUserNameMap } from 'store/api/authApi';
import {
    type ArchivedRequestSummary,
    useGetArchivedRequestsQuery,
} from 'store/api/payerRequestApi';
import { useGridState } from 'store/grids/gridsThunks';

const initialSortProperties = [{ id: 'completed_date', desc: true }];

const typeLevel2Keys = () =>
    MemberInquiryEscalationOptionTree.flatMap(
        // The unknown is required by eslint -- it would run well without it.
        (item) =>
            item.typeLevel2 as unknown as { name: string; secondaryData: string | undefined }[],
    ).map((item) => item.name);

const timeFrameConfig = {
    key: 'completed_date',
    title: 'Request Time Frame',
    defaultValue: TimeFrameOptions.PAST_WEEK,
    optionOverrideMap: {
        [TimeFrameOptions.PAST_DAY]: { text: 'Today' },
        [TimeFrameOptions.PAST_WEEK]: { text: 'Last 7 Days' },
        [TimeFrameOptions.PAST_MONTH]: { text: 'Last 30 Days' },
        [TimeFrameOptions.PAST_QUARTER]: { text: 'Last 3 Months' },
        [TimeFrameOptions.PAST_YEAR]: { text: 'Last 12 Months' },
        ALL_TIME: { text: 'All', handler: () => true },
    },
};

const filterCategories = (
    permissions: string[],
    filters:
        | {
              key: string;
              values: string[];
          }[]
        | undefined,
    subSubtypesFeatureFlag: boolean,
): FilterCategory<ArchivedRequestSummary>[] => [
    {
        key: 'request_type',
        title: 'Request Category',
        customValues: Object.entries(ReadPermissionRequestTypeMap)
            // Only show requestTypes user has permissions for
            .filter(([permission]) => permissions.includes(permission))
            .map(([_, value]) => ({
                value,
                text: RequestTypeDisplayTextMap[value],
            })),
    },
    {
        key: 'request_sub_type',
        title: 'Request Type',
        customValues: [
            ...new Set([
                ...Object.values(ChangeRequestSubType),
                ...Object.values(MemberEscalationSubType),
            ]),
        ]
            .sort(sortAsc)
            .map((value) => ({ value, text: value })),
    },
    ...(subSubtypesFeatureFlag
        ? [
              {
                  key: 'type_level_2',
                  title: 'Request Subtype',
                  customValues: [...new Set(typeLevel2Keys())]
                      .sort(sortAsc)
                      .map((value) => ({ value, text: value })),
              } as FilterCategory<ArchivedRequestSummary>,
          ]
        : []),
    ...(subSubtypesFeatureFlag
        ? []
        : [
              {
                  key: 'issue_source',
                  title: 'Issue Source',
                  customValues: filters
                      ?.find((filter) => filter.key === 'issue_source')
                      ?.values?.map((value) => ({ value, text: value })),
              } as FilterCategory<ArchivedRequestSummary>,
          ]),
    {
        key: 'request_status',
        title: 'Status',
        customValues: [
            PayerRequestStatusIndicator.Statuses.COMPLETED,
            PayerRequestStatusIndicator.Statuses.CANCELED,
        ].map((value) => ({ value, text: PayerRequestStatusIndicator.StatusMessages[value] })),
    },
    {
        key: 'urgent',
        title: 'Request Urgency',
        customValues: [{ value: 'true', text: 'Urgent' }],
    },
    {
        key: 'pbm_only',
        title: 'Visibility',
        customValues: [{ value: 'true', text: 'PBM Only' }],
    },
];

const columns = (subsubTypeFeatureFlag: boolean): ColumnDef<ArchivedRequestSummary>[] => [
    {
        accessorKey: 'request_type',
        header: 'Request Category',
        size: 200,
        cell: ({ getValue }) => <PrimaryData data={RequestTypeDisplayTextMap[getValue()]} />,
    },
    {
        accessorKey: 'member_id',
        header: 'Member ID',
        enableSorting: false,
        size: 120,
    },
    {
        accessorKey: 'request_sub_type',
        header: 'Request Type',
        size: 150,
    },
    ...(subsubTypeFeatureFlag
        ? [
              {
                  accessorKey: 'type_level_2',
                  header: 'Request Subtype',
                  size: 150,
              } as ColumnDef<ArchivedRequestSummary>,
          ]
        : []),
    {
        accessorKey: 'resolved_at',
        header: 'Resolved Date',
        size: 160,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        accessorKey: 'completed_date',
        header: 'Closed On',
        size: 160,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        accessorKey: 'submitter_upn',
        header: 'Created By',
        size: 150,
        cell: ({ getValue, row }) => (
            <UserCell upn={getValue()} fullName={row.original.submitter_full_name} small />
        ),
    },
    {
        accessorKey: 'completed_by',
        header: 'Closed By',
        size: 120,
        cell: ({ getValue, row }) => (
            <SecondaryData data={row.original.completed_by_full_name ?? getValue()} />
        ),
    },
    {
        accessorKey: 'is_unread',
        header: () => <Icon icon={IconNames.CHAT} />,
        enableSorting: false,
        size: 48,
        cell: ({ getValue }) => <RequestChatIcon isUnread={getValue()} />,
    },
    {
        accessorKey: 'urgent',
        header: () => <Icon icon={IconNames.WARNING_SIGN} intent={Icon.Intent.WARNING} />,
        enableSorting: false,
        size: 48,
        cell: ({ getValue }) =>
            getValue() ? (
                <Icon icon={IconNames.WARNING_SIGN} intent={Icon.Intent.WARNING} title="Urgent" />
            ) : null,
    },
    {
        accessorKey: 'request_number',
        header: 'Request #',
        size: 90,
        cell: ({ getValue, row }) => (
            <PayerRequestNumber text={getValue()} pbmOnly={row.original.pbm_only} />
        ),
    },
    ...(subsubTypeFeatureFlag
        ? []
        : [
              {
                  accessorKey: 'issue_source',
                  header: 'Issue Source',
                  size: 120,
              } as ColumnDef<ArchivedRequestSummary>,
          ]),
    {
        accessorKey: 'request_status',
        header: 'Status',
        size: 120,
        cell: ({ getValue }) => (
            <PayerRequestStatusIndicator
                status={
                    getValue() === PayerRequestStatusIndicator.Statuses.RESOLVED
                        ? PayerRequestStatusIndicator.Statuses.MONITORING
                        : getValue()
                }
            />
        ),
    },
];

export const RequestArchiveGrid = () => {
    const navigate = useNavigate();
    const { showPayerRequestSubSubTypes } = useFlags<{
        showPayerRequestSubSubTypes: boolean;
    }>();
    const { permissions, segmentIds } = useAppSelector((state) => state.auth);
    const { payerId, segmentId } = useAppSelector((state) => state.views);

    const {
        sort,
        search,
        activeFilters,
        filterPaneWidth,
        page,
        updateGridSort,
        updateGridSearch,
        updateGridActiveFilters,
        updateFilterPaneWidth,
        updateCurrentPage,
    } = useGridState(RequestType.ARCHIVE, initialSortProperties);

    const apiQueryParams =
        payerId && activeFilters
            ? {
                  page,
                  payerId,
                  segmentIds: segmentId ? [segmentId] : segmentIds,
                  searchText: search,
                  timeframe: activeFilters.completed_date?.[0] as TimeFrameOption,
                  requestType:
                      activeFilters.request_type as (typeof RequestType)[keyof typeof RequestType][],
                  requestSubType: activeFilters.request_sub_type,
                  typeLevel2: activeFilters.type_level_2,
                  issueSource: activeFilters.issue_source,
                  requestStatus: activeFilters.request_status as PayerRequestStatus[],
                  urgent: activeFilters.urgent?.[0] as never as boolean,
                  pbmOnly: activeFilters.pbm_only?.[0] as never as boolean,
                  sort,
              }
            : undefined;
    const { data: firstPageData, isFetching: isFirstPageDataFetching } =
        useGetArchivedRequestsQuery(apiQueryParams ? { ...apiQueryParams, page: 0 } : skipToken);
    const { data: currentPageData, isFetching: isCurrentPageDataFetching } =
        useGetArchivedRequestsQuery(apiQueryParams ?? skipToken);

    const userNameMap = useUserNameMap(payerId);
    const requests = useMemo(
        () =>
            currentPageData?.requests?.map((request) => ({
                ...request,
                submitter_full_name: userNameMap?.[request.submitter_upn],
                completed_by_full_name: request.completed_by && userNameMap?.[request.completed_by],
            })),
        [currentPageData?.requests, userNameMap],
    );

    return (
        <GridWithFilters
            viewsPaneContent={<ViewsControls />}
            data={requests}
            isLoading={isFirstPageDataFetching || isCurrentPageDataFetching}
            columns={columns(showPayerRequestSubSubTypes)}
            manualSorting
            sortProperties={sort}
            onSort={updateGridSort}
            enableSearch
            searchText={search}
            onSearch={updateGridSearch}
            timeFrameConfig={timeFrameConfig}
            filterCategories={filterCategories(
                permissions,
                firstPageData?.filters,
                showPayerRequestSubSubTypes,
            )}
            activeFilters={activeFilters}
            onFilter={updateGridActiveFilters}
            filterPaneRenderWidth={filterPaneWidth}
            onFilterPaneResize={updateFilterPaneWidth}
            currentPage={page}
            onPageChange={updateCurrentPage}
            totalCount={firstPageData?.count}
            onRowClick={(rowData) =>
                navigate(AppRoutes.requestDetails(rowData.payer_id, rowData.request_id))
            }
        />
    );
};
